import React, { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  Clock,
  DollarSign,
  RefreshCw,
  Ticket,
  TrendingUp,
  Users,
} from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  BuyAnalyticsResponse,
  buyAdminApi,
} from "../../../services/buyAdminApi";
import {
  MEMBER_SAVINGS_LABEL,
  PURCHASING_MEMBERS_LABEL,
  TICKETS_SOLD_LABEL,
  REVENUE_LABEL,
  timeframeHint,
} from "../shared/buyAdminMetricLabels";
import BuyAdminThemeToggle from "../shared/BuyAdminThemeToggle";
import "../shared/buyAdminThemeGlobals.css";
import styles from "./BuyAnalytics.module.css";

const timeframes = ["7d", "30d", "90d", "12m"];

const formatCurrency = (value: number | null | undefined, digits = 0) =>
  Number(value || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

const formatNumber = (value: number | null | undefined) => Number(value || 0).toLocaleString("en-US");

const formatChange = (value: number) => {
  const sign = value > 0 ? "+" : "";
  return `${sign}${value}% vs last period`;
};

const emptyAnalytics: BuyAnalyticsResponse = {
  window: { start: "", end: "", previousStart: "", previousEnd: "" },
  key_metrics: {
    active_members: 0,
    revenue: 0,
    tickets_sold: 0,
    member_savings: 0,
    new_signups: 0,
    changes: { active_members: 0, revenue: 0, tickets_sold: 0, member_savings: 0, new_signups: 0 },
  },
  purchase_timing: { total: 0, buckets: [] },
  revenue_growth: [],
  cohort_retention: [],
  purchase_behavior: [],
  top_events: [],
};

const timingColorClass: Record<string, string> = {
  red: styles.timingRed,
  orange: styles.timingOrange,
  amber: styles.timingAmber,
  yellow: styles.timingYellow,
  lime: styles.timingLime,
  emerald: styles.timingEmerald,
  blue: styles.timingBlue,
  slate: styles.timingSlate,
};

const BuyAdminBuyAnalytics = () => {
  const [timeframe, setTimeframe] = useState("30d");
  const [analytics, setAnalytics] = useState<BuyAnalyticsResponse>(emptyAnalytics);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const loadAnalytics = async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const result = await buyAdminApi.getBuyAnalytics({
        timeframe,
      });
      setAnalytics(result);
    } catch (error: any) {
      setErrorMessage(error?.message || "Failed to load buy analytics.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeframe]);

  const lastMinute = useMemo(() => {
    const buckets = analytics.purchase_timing.buckets;
    const dayOf = buckets.find((item) => item.bucket === "Day of Event");
    const oneDay = buckets.find((item) => item.bucket === "1 Day Before");
    return {
      pct: Number(((dayOf?.pct || 0) + (oneDay?.pct || 0)).toFixed(1)),
      count: (dayOf?.count || 0) + (oneDay?.count || 0),
    };
  }, [analytics.purchase_timing.buckets]);

  const advance = useMemo(() => {
    const buckets = analytics.purchase_timing.buckets;
    const oneTwoWeeks = buckets.find((item) => item.bucket === "1-2 Weeks Before");
    const twoFour = buckets.find((item) => item.bucket === "2-4 Weeks Before");
    const twoMonths = buckets.find((item) => item.bucket === "2+ Months Before");
    return {
      pct: Number(((oneTwoWeeks?.pct || 0) + (twoFour?.pct || 0) + (twoMonths?.pct || 0)).toFixed(1)),
      count: (oneTwoWeeks?.count || 0) + (twoFour?.count || 0) + (twoMonths?.count || 0),
    };
  }, [analytics.purchase_timing.buckets]);

  const periodHint = timeframeHint(timeframe);

  const primeWindowPct = useMemo(() => {
    const buckets = analytics.purchase_timing.buckets;
    const three = buckets.find((item) => item.bucket === "3 Days Before");
    const seven = buckets.find((item) => item.bucket === "7 Days Before");
    return Number(((three?.pct || 0) + (seven?.pct || 0)).toFixed(1));
  }, [analytics.purchase_timing.buckets]);

  return (
    <div className={`${styles.page} buy-admin-theme-scope`} data-testid="buy-analytics-page">
      <div className={styles.header}>
        <div className={styles.titleBlock}>
          <h1>Buy Analytics</h1>
          <p>Member and purchase insights</p>
        </div>
        <div className={`${styles.headerActions} buy-admin-theme-actions`}>
          {timeframes.map((item) => (
            <button
              key={item}
              className={`${styles.timeframeButton} ${timeframe === item ? styles.timeframeButtonActive : ""}`}
              onClick={() => {
                setTimeframe(item);
              }}
              type="button"
            >
              {item}
            </button>
          ))}
          <button
            type="button"
            className={styles.refreshButton}
            onClick={() => void loadAnalytics()}
            disabled={loading}
            aria-label="Refresh buy analytics"
            title="Refresh"
          >
            <RefreshCw size={16} className={loading ? styles.spin : undefined} />
          </button>
          <BuyAdminThemeToggle iconOnly />
        </div>
      </div>

      {errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}

      <section className={styles.card}>
        <div className={styles.cardHeader}>
          <h2>Key Metrics</h2>
          {loading && <span>Loading...</span>}
        </div>
        <div className={styles.metricsGrid}>
          <div className={`${styles.metricTile} buy-admin-stat-card buy-admin-stat-card--blue`}>
            <div className="buy-admin-stat-head">
              <div className="buy-admin-stat-icon">
                <Users size={18} />
              </div>
              <span className="buy-admin-stat-label">{PURCHASING_MEMBERS_LABEL}</span>
            </div>
            <small>{periodHint}</small>
            <strong>{formatNumber(analytics.key_metrics.active_members)}</strong>
            <em className={analytics.key_metrics.changes.active_members >= 0 ? styles.positive : styles.negative}>
              {formatChange(analytics.key_metrics.changes.active_members)}
            </em>
          </div>
          <div className={`${styles.metricTile} buy-admin-stat-card buy-admin-stat-card--green`}>
            <div className="buy-admin-stat-head">
              <div className="buy-admin-stat-icon">
                <DollarSign size={18} />
              </div>
              <span className="buy-admin-stat-label">{REVENUE_LABEL}</span>
            </div>
            <small>{periodHint}</small>
            <strong>{formatCurrency(analytics.key_metrics.revenue)}</strong>
            <em className={analytics.key_metrics.changes.revenue >= 0 ? styles.positive : styles.negative}>
              {formatChange(analytics.key_metrics.changes.revenue)}
            </em>
          </div>
          <div className={`${styles.metricTile} buy-admin-stat-card buy-admin-stat-card--purple`}>
            <div className="buy-admin-stat-head">
              <div className="buy-admin-stat-icon">
                <Ticket size={18} />
              </div>
              <span className="buy-admin-stat-label">{TICKETS_SOLD_LABEL}</span>
            </div>
            <small>{periodHint}</small>
            <strong>{formatNumber(analytics.key_metrics.tickets_sold)}</strong>
            <em className={analytics.key_metrics.changes.tickets_sold >= 0 ? styles.positive : styles.negative}>
              {formatChange(analytics.key_metrics.changes.tickets_sold)}
            </em>
          </div>
          <div className={`${styles.metricTile} buy-admin-stat-card buy-admin-stat-card--amber`}>
            <div className="buy-admin-stat-head">
              <div className="buy-admin-stat-icon">
                <TrendingUp size={18} />
              </div>
              <span className="buy-admin-stat-label">{MEMBER_SAVINGS_LABEL}</span>
            </div>
            <small>{periodHint}</small>
            <strong className={styles.savingsValue}>{formatCurrency(analytics.key_metrics.member_savings)}</strong>
            <em className={analytics.key_metrics.changes.member_savings >= 0 ? styles.positive : styles.negative}>
              {formatChange(analytics.key_metrics.changes.member_savings)}
            </em>
          </div>
        </div>
      </section>

      <section className={`${styles.card} ${styles.timingCard}`}>
        <div className={styles.cardHeader}>
          <div>
            <h2><Clock size={20} /> Purchase Timing Analysis</h2>
            <p>When members purchase tickets relative to event date</p>
          </div>
        </div>
        <div className={styles.timingLayout}>
          <div className={styles.barStack}>
            {analytics.purchase_timing.buckets.map((item) => (
              <div key={item.bucket} className={styles.barItem}>
                <div className={styles.barMeta}>
                  <span>{item.bucket}</span>
                  <strong>{formatNumber(item.count)} line items ({item.pct}%)</strong>
                </div>
                <div className={styles.barTrack}>
                  <div
                    className={`${styles.barFill} ${timingColorClass[item.color] || styles.timingSlate}`}
                    style={{ width: `${Math.min(100, Math.max(2, item.pct * 4))}%` }}
                  >
                    {item.pct > 4 && <span>{item.pct}%</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className={styles.insights}>
            <div className={styles.insightGrid}>
              <div className={styles.lastMinuteCard}>
                <span>Last Minute (&lt;=1 day)</span>
                <strong>{lastMinute.pct}%</strong>
                <em>{formatNumber(lastMinute.count)} line items</em>
              </div>
              <div className={styles.advanceCard}>
                <span>Advance (2+ weeks)</span>
                <strong>{advance.pct}%</strong>
                <em>{formatNumber(advance.count)} line items</em>
              </div>
            </div>
            <div className={styles.insightBox}>
              <h3>Insights</h3>
              <p><AlertTriangle size={16} /> <strong>{lastMinute.pct}%</strong> of order line items are purchased within 1 day of the event.</p>
              <p><TrendingUp size={16} /> <strong>{primeWindowPct}%</strong> purchase 3-7 days before, the prime pricing window.</p>
              <p><Clock size={16} /> <strong>{advance.pct}%</strong> purchase 2+ weeks ahead.</p>
            </div>
            <div className={styles.totalAnalyzed}>
              <span>Order Line Items Analyzed</span>
              <strong>{formatNumber(analytics.purchase_timing.total)}</strong>
              <em>{formatNumber(analytics.key_metrics.tickets_sold)} tickets in period</em>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.card}>
        <div className={styles.cardHeader}>
          <h2>Revenue & Member Growth</h2>
        </div>
        <div className={styles.rechartsPanel}>
          {analytics.revenue_growth.length === 0 && <div className={styles.emptyState}>No revenue data found for this period</div>}
          {analytics.revenue_growth.length > 0 && (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={analytics.revenue_growth}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--buy-chart-grid)" />
                <XAxis dataKey="month" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip
                  formatter={(value: number | string, name: string) => {
                    if (name === "Revenue ($)") return [formatCurrency(Number(value), 2), name];
                    return [formatNumber(Number(value)), name];
                  }}
                />
                <Legend />
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="revenue"
                  stroke="#5D75F8"
                  fill="#5D75F8"
                  fillOpacity={0.2}
                  name="Revenue ($)"
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="members"
                  stroke="#10b981"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  name="Members"
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </section>

      <div className={styles.twoColumnGrid}>
        <section className={styles.card}>
          <div className={styles.cardHeader}>
            <h2>Cohort Retention Analysis</h2>
          </div>
          <div className={styles.tableWrap}>
            <table className={styles.plainTable}>
              <thead>
                <tr>
                  <th>Cohort</th>
                  <th>M1</th>
                  <th>M2</th>
                  <th>M3</th>
                  <th>M6</th>
                  <th>M12</th>
                </tr>
              </thead>
              <tbody>
                {analytics.cohort_retention.map((row) => (
                  <tr key={row.cohort}>
                    <td>{row.cohort}</td>
                    <td><span className={styles.badgeGreen}>{row.m1}%</span></td>
                    <td><span className={styles.badgeGreen}>{row.m2}%</span></td>
                    <td><span className={styles.badgeBlue}>{row.m3}%</span></td>
                    <td><span className={styles.badgeAmber}>{row.m6}%</span></td>
                    <td><span className={styles.badgePurple}>{row.m12}%</span></td>
                  </tr>
                ))}
                {analytics.cohort_retention.length === 0 && (
                  <tr><td colSpan={6}>No cohort data found for this period</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className={styles.card}>
          <div className={styles.cardHeader}>
            <h2>Purchase Behavior Distribution</h2>
          </div>
          <div className={styles.behaviorStack}>
            {analytics.purchase_behavior.map((item) => (
              <div key={item.category} className={styles.behaviorItem}>
                <div className={styles.barMeta}>
                  <span>{item.category}</span>
                  <strong>{formatNumber(item.count)} orders ({item.pct}%)</strong>
                </div>
                <div className={styles.smallTrack}>
                  <div style={{ width: `${Math.max(2, item.pct)}%` }} />
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      <section className={styles.card}>
        <div className={styles.cardHeader}>
          <h2>Top Performing Events</h2>
        </div>
        <div className={styles.tableWrap}>
          <table className={styles.eventsTable}>
            <thead>
              <tr>
                <th>Event</th>
                <th>Tickets Sold</th>
                <th>Revenue</th>
                <th>Member Savings</th>
                <th>Avg Price</th>
              </tr>
            </thead>
            <tbody>
              {analytics.top_events.map((event) => (
                <tr key={event.event}>
                  <td>{event.event}</td>
                  <td>{formatNumber(event.tickets_sold)}</td>
                  <td>{formatCurrency(event.revenue)}</td>
                  <td className={styles.savingsCell}>{formatCurrency(event.member_savings)}</td>
                  <td>{formatCurrency(event.avg_price)}</td>
                </tr>
              ))}
              {analytics.top_events.length === 0 && (
                <tr><td colSpan={5}>No events found for this period</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default BuyAdminBuyAnalytics;
